import { DateOnly, format } from '@if/core/date'
import {
  EApplicantType,
  ECreditApplicationStatus,
  EDecision,
  EOriginSystemId,
  EPaymentPlan,
  EProgressionStatus,
} from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  BodyText,
  Caption,
  type ColumnDef,
  type DataGridPageQueryParams,
  type DataGridPageQueryResult,
  type EnumStepperState,
  Placeholder,
  useIfUser,
} from '@if/mui'
import { List, ListItem, Stack } from '@mui/material'
import { useCreditApplicationList } from '@src/data/api/credit-api/credit-api'
import { CreditDashboardFilters } from '@src/data/api/type'
import { programConfigByFinancingProgramId, programStepsByFinancingProgramId } from '@src/data/creditapp-selectors'
import { calculateDaysUntilExpiration } from '@src/services/Formatter'
import { ECurrentCustomerStep } from '@src/types/Constants'
import { CreditDashboardEntry } from '@src/types/CreditDashboardEntry'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { Bell, Eye, FileUp, MessageSquare } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getEndDate, getStartDate } from './browseCreditApplicationPage-selector'

/**
 * Collapses an application's real decision state into the one modifier the
 * `enumStepper` column takes, from the same fields as `CreditApplicationProgressUtils`.
 * - `rejected` — declined at a decision gate, or the application is over.
 * - `active`   — progressing normally; a booked/funded app reads as done because
 *   `active` on the last step (Funding) completes the rail.
 * - `blocked`  — needs attention, or on Funding but not yet booked, so an
 *   in-progress Funding application doesn't wrongly read as complete.
 */
const getCreditStepperState = (row: CreditDashboardEntry): EnumStepperState => {
  if (row.prequalificationDecision === EDecision.Refused || row.hardHitDecision === EDecision.Refused) {
    return 'rejected'
  }
  if (
    row.creditApplicationStatus === ECreditApplicationStatus.Cancelled ||
    row.creditApplicationStatus === ECreditApplicationStatus.Expired
  ) {
    return 'rejected'
  }
  if (
    row.progressionStatus === EProgressionStatus.Booked ||
    row.progressionStatus === EProgressionStatus.PaymentPending
  ) {
    return 'active'
  }
  if (
    row.requiresMerchantAttention ||
    row.hasDocumentsPendingReview ||
    row.missingRequiredDocuments.length > 0 ||
    row.currentCustomerStep === ECurrentCustomerStep.Funding
  ) {
    return 'blocked'
  }
  return 'active'
}

/**
 * Filter schema for the applications DataGridPage. `creditApplicationStatus` is
 * the tabs field (Active/Draft); the filter dialog can override it with other
 * statuses (Cancelled/Completed/Expired/All), in which case no tab highlights —
 * DataGridPageTabs guards an out-of-range value. Module scope (referentially stable).
 */
export const applicationsFilterSchema = yup.object({
  search: yup.text().default(''),
  creditApplicationStatus: yup.text().default(ECreditApplicationStatus.Active),
  merchantIds: yup.array(yup.text().required()).default([]),
  currentCustomerStep: yup.text().default(''),
  requiresMerchantAttention: yup.boolean(),
  startDate: yup.dateOnly().default(() => new DateOnly(getStartDate())),
  endDate: yup.dateOnly().default(() => new DateOnly(getEndDate())),
  onlyEditableApplications: yup.boolean(),
  onlyCurrentUserApplications: yup.boolean(),
})

export type ApplicationsFilters = yup.InferSubmit<typeof applicationsFilterSchema>

/** Adapts DataGridPage's {filters, pagination} to the unified searchTerm query API. */
export function useApplicationsData({
  filters,
  pagination,
}: DataGridPageQueryParams<CreditDashboardEntry, ApplicationsFilters>): DataGridPageQueryResult<CreditDashboardEntry> {
  const user = useIfUser()
  const query: CreditDashboardFilters = {
    limit: pagination.limit,
    offset: pagination.offset,
    searchTerm: filters.search ?? '',
    creditApplicationStatus: filters.creditApplicationStatus ?? '',
    merchantIds: filters.merchantIds ?? [],
    currentCustomerStep: filters.currentCustomerStep ?? '',
    requiresMerchantAttention: filters.requiresMerchantAttention ?? false,
    startDate: filters.startDate ? filters.startDate.toString() : getStartDate(),
    endDate: filters.endDate ? filters.endDate.toString() : getEndDate(),
    originSystemId: filters.onlyEditableApplications ? EOriginSystemId.MerchantDashboard : '',
    createdByUserId: filters.onlyCurrentUserApplications && user ? user.id : '',
    // Read from the URL by the old query but never set by the UI — keep as API defaults.
    financingProgramIds: [],
    territoryIds: [],
    plan: [],
    progressionStatus: [],
    dateRange: '',
  }

  const [data, isLoading] = useCreditApplicationList(query)
  return { data, isLoading }
}

export function useApplicationsColumns(
  isVeterinary: boolean,
  hasDetailAccess: boolean,
  unreadCreditApplicationIds?: Set<string>,
): ColumnDef<CreditDashboardEntry>[] {
  const { t, i18n } = useTranslation()

  // Localized document name for the required-documents notification tooltip.
  const getFullDocumentName = useCallback(
    (doc: RequiredDocument) => {
      let applicantSuffix = ''
      if (doc.applicantType === EApplicantType.Applicant) applicantSuffix = ` - ${t('common.applicant')}`
      else if (doc.applicantType === EApplicantType.CoApplicant) applicantSuffix = ` - ${t('common.coApplicant')}`
      return i18n.resolvedLanguage === 'fr' ? `${doc.nameFr}${applicantSuffix}` : `${doc.nameEn}${applicantSuffix}`
    },
    [t, i18n.resolvedLanguage],
  )

  return useMemo<ColumnDef<CreditDashboardEntry>[]>(
    () => [
      {
        name: 'financingProgramId',
        header: t('applicationHeader.program'),
        align: 'center',
        minWidth: 140,
        getFormatted: (_value, row) => t(`EFinancingProgram.${row.financingProgramId}`, { ns: 'enum' }),
      },
      {
        name: 'creditApplicationReferenceNumber',
        type: 'reference',
        header: t('applicationHeader.id'),
        align: 'left',
        minWidth: 80,
        href: hasDetailAccess
          ? (row) =>
              row.creditApplicationStatus === ECreditApplicationStatus.Draft
                ? `/credits/${row.financingProgramId}/${row.id}/edit`
                : `/credits/${row.financingProgramId}/${row.id}`
          : undefined,
      },
      {
        name: 'merchantName',
        header: t('applicationHeader.merchant'),
        minWidth: 150,
        flexGrow: 3,
      },
      {
        name: 'clientName',
        type: 'titleSubtitle',
        header: t('applicationHeader.name'),
        minWidth: 150,
        flexGrow: 2,
        getSubtitle: (row) => {
          const cfg = programConfigByFinancingProgramId(row.financingProgramId)
          if (!row.beneficiaryName || !cfg.beneficiaryIsRequired) return null
          return isVeterinary
            ? `${t('common.petName')}: ${row.beneficiaryName}`
            : `${t('common.beneficiary')}: ${row.beneficiaryName}`
        },
      },
      {
        name: 'maxAmountFinanced',
        type: 'currency',
        header: t('applicationHeader.approvedLimit'),
        minWidth: 100,
        flexGrow: 1,
        // Let `type: 'currency'` format; only null out the value for programs
        // that don't use a max payment.
        getValue: (row) =>
          programConfigByFinancingProgramId(row.financingProgramId).useMaxPayment ? row.maxAmountFinanced : null,
      },
      {
        name: 'interestRate',
        header: t('applicationHeader.rate'),
        align: 'center',
        minWidth: 90,
        flexGrow: 0.75,
        getFormatted: (_value, row) => {
          const rate = row.interestRate
          if (!rate) return ''
          if (row.paymentPlanId !== EPaymentPlan.RegularDailyInterests) return 'PROMO'
          return `${rate} %`
        },
      },
      {
        name: 'amountRequested',
        type: 'currency',
        header: t('applicationHeader.loan'),
        minWidth: 100,
        flexGrow: 1,
        emptyPlaceholder: <Placeholder>{t('creditStatus.pending')}</Placeholder>,
        getFormatted: (value) =>
          value ? formatCurrencyNoDecimals(value) : <Placeholder>{t('creditStatus.pending')}</Placeholder>,
      },
      {
        name: 'currentCustomerStep',
        type: 'enumStepper',
        header: t('applicationHeader.status'),
        minWidth: 200,
        flexGrow: 1.75,
        set: ECurrentCustomerStep,
        getState: getCreditStepperState,
        // The rail shows the program's own stages, so a Products row reads 3 and not 5.
        getSteps: (row) => programStepsByFinancingProgramId(row.financingProgramId),
      },
      {
        name: 'createdOn',
        header: t('applicationHeader.date'),
        minWidth: 130,
        flexGrow: 1.25,
        renderCell: (row) => {
          const daysUntilExpiration = row.expiresOn ? calculateDaysUntilExpiration(row.expiresOn) : null
          return (
            <Stack>
              {row.createdOn && <BodyText>{format(new Date(row.createdOn), 'PP')}</BodyText>}
              {daysUntilExpiration !== null &&
                (daysUntilExpiration <= 0 ? (
                  <Placeholder sx={{ color: 'error.main' }}>{t('applicationHeader.expired')}</Placeholder>
                ) : (
                  <Caption sx={{ color: 'warning.main' }}>
                    {t('applicationHeader.daysLeft', { count: daysUntilExpiration })}
                  </Caption>
                ))}
            </Stack>
          )
        },
      },
      {
        name: 'merchantReferenceCode',
        header: t('applicationHeader.yourReferenceCode'),
        align: 'center',
        minWidth: 80,
        flexGrow: 2,
        emptyPlaceholder: <Placeholder>-</Placeholder>,
      },
      {
        type: 'action',
        name: 'notifications',
        header: 'Action',
        actions: [
          // Unread chat: a dot-badged indicator, shown only when there are unreads.
          {
            icon: MessageSquare,
            label: t('common.unreadMessages'),
            hidden: (row) => !(unreadCreditApplicationIds?.has(row.id) ?? false),
            badge: () => 'dot',
          },
          // Merchant notifications: bell with the list of notification titles.
          {
            icon: Bell,
            label: t('common.notifications'),
            hidden: (row) => row.merchantNotificationCodes.length === 0,
            tooltip: (row) => (
              <>
                {row.merchantNotificationCodes.map((code, index) => (
                  <div key={code}>
                    {t(`notificationsTitles.${code}`)}
                    {index !== row.merchantNotificationCodes.length - 1 && (
                      <>
                        <br />
                        <br />
                      </>
                    )}
                  </div>
                ))}
              </>
            ),
          },
          // Missing required documents (merchant-dashboard applications only).
          {
            icon: FileUp,
            label: t('common.requiredDocuments'),
            hidden: (row) =>
              !(row.missingRequiredDocuments.length > 0 && row.originSystemId === EOriginSystemId.MerchantDashboard),
            tooltip: (row) => (
              <List>
                {t('common.requiredDocuments')}
                {row.missingRequiredDocuments.map((doc) => (
                  <ListItem key={doc.typeId}>{getFullDocumentName(doc)}</ListItem>
                ))}
              </List>
            ),
          },
          // Limited access when the application originated outside the dashboard.
          {
            icon: Eye,
            label: t('viewCreditApplication.limitedAccess'),
            hidden: (row) => row.originSystemId === EOriginSystemId.MerchantDashboard,
            tooltip: (row) =>
              row.originSystemId === EOriginSystemId.Backoffice
                ? t('viewCreditApplication.limitedAccessBackOffice')
                : t('viewCreditApplication.limitedAccessSubscription'),
          },
        ],
      },
    ],
    [t, isVeterinary, hasDetailAccess, unreadCreditApplicationIds, getFullDocumentName],
  )
}
